import { UniversalMapEntry } from './MapInterface';

/**
 * StaticMapCache
 * 
 * Provides caching for static map URLs to reduce redundant API calls
 * and improve performance by reusing previously generated URLs.
 */

interface StaticMapParams {
  provider: 'google' | 'mapbox';
  center?: { lat: number; lng: number };
  zoom: number;
  width: number;
  height: number;
  entries: UniversalMapEntry[];
  mapType?: string;
  showMarkers?: boolean;
}

interface CacheEntry {
  url: string;
  timestamp: number;
  accessCount: number;
}

class StaticMapCache {
  private cache = new Map<string, CacheEntry>();
  private maxAge = 1000 * 60 * 60; // 1 hour default
  private maxSize = 100; // Maximum cache entries
  
  constructor(maxAge?: number, maxSize?: number) {
    if (maxAge) this.maxAge = maxAge;
    if (maxSize) this.maxSize = maxSize;
    
    // Periodic cleanup
    setInterval(() => this.cleanup(), this.maxAge / 4);
  }

  /**
   * Generate a deterministic cache key from map parameters
   */
  generateCacheKey(params: StaticMapParams): string {
    // Create a normalized representation for consistent caching
    const normalizedEntries = params.entries
      .map(entry => ({
        lat: Math.round(entry.location.lat * 1000000) / 1000000, // 6 decimal precision
        lng: Math.round(entry.location.lng * 1000000) / 1000000
      }))
      .sort((a, b) => a.lat - b.lat || a.lng - b.lng); // Sort for consistency

    const keyData = {
      provider: params.provider,
      center: params.center ? {
        lat: Math.round(params.center.lat * 1000000) / 1000000,
        lng: Math.round(params.center.lng * 1000000) / 1000000
      } : null,
      zoom: params.zoom,
      width: params.width,
      height: params.height,
      entries: normalizedEntries,
      mapType: params.mapType || 'roadmap',
      showMarkers: params.showMarkers !== false
    };

    return btoa(JSON.stringify(keyData)).replace(/[/+=]/g, ''); // Base64 encode and clean
  }

  /**
   * Get cached URL if available and not expired
   */
  getCachedUrl(key: string): string | null {
    const entry = this.cache.get(key);
    
    if (!entry) {
      return null;
    }

    // Check if expired
    if (Date.now() - entry.timestamp > this.maxAge) {
      this.cache.delete(key);
      return null;
    }

    // Update access count and timestamp for LRU
    entry.accessCount++;
    entry.timestamp = Date.now();
    
    return entry.url;
  }

  /**
   * Store URL in cache
   */
  setCachedUrl(key: string, url: string): void {
    // Enforce cache size limit
    if (this.cache.size >= this.maxSize) {
      this.evictLeastRecentlyUsed();
    }

    this.cache.set(key, {
      url,
      timestamp: Date.now(),
      accessCount: 1
    });
  }

  /**
   * Get cache statistics
   */
  getStats(): { size: number; hitRate: number; maxSize: number; maxAge: number } {
    return {
      size: this.cache.size,
      hitRate: this.calculateHitRate(),
      maxSize: this.maxSize,
      maxAge: this.maxAge
    };
  }

  /**
   * Clear all cached entries
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Remove expired entries
   */
  private cleanup(): void {
    const now = Date.now();
    const expiredKeys: string[] = [];

    this.cache.forEach((entry, key) => {
      if (now - entry.timestamp > this.maxAge) {
        expiredKeys.push(key);
      }
    });

    expiredKeys.forEach(key => this.cache.delete(key));

    if (process.env.NODE_ENV === 'development') {
      console.log(`StaticMapCache: Cleaned up ${expiredKeys.length} expired entries`);
    }
  }

  /**
   * Remove least recently used entry
   */
  private evictLeastRecentlyUsed(): void {
    let oldestKey: string | null = null;
    let oldestTimestamp = Date.now();

    this.cache.forEach((entry, key) => {
      if (entry.timestamp < oldestTimestamp) {
        oldestTimestamp = entry.timestamp;
        oldestKey = key;
      }
    });

    if (oldestKey) {
      this.cache.delete(oldestKey);
    }
  }

  /**
   * Calculate cache hit rate (placeholder - would need to track misses)
   */
  private calculateHitRate(): number {
    // This is a simplified implementation
    // In a real scenario, you'd track hits vs misses
    return this.cache.size > 0 ? 0.85 : 0; // Estimated hit rate
  }
}

// Global cache instance
export const staticMapCache = new StaticMapCache();

/**
 * Hook for using static map cache with React components
 */
export const useStaticMapCache = () => {
  const getCachedMapUrl = (params: StaticMapParams): string | null => {
    const key = staticMapCache.generateCacheKey(params);
    return staticMapCache.getCachedUrl(key);
  };

  const setCachedMapUrl = (params: StaticMapParams, url: string): void => {
    const key = staticMapCache.generateCacheKey(params);
    staticMapCache.setCachedUrl(key, url);
  };

  const getCacheStats = () => staticMapCache.getStats();
  
  const clearCache = () => staticMapCache.clear();

  return {
    getCachedMapUrl,
    setCachedMapUrl,
    getCacheStats,
    clearCache
  };
};

export default StaticMapCache;